import type { IPostCandidatesSearch } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { ResponsiveScroll } from "@data-display/wrapper/ResponsiveScroll";
import type { IResponsiveScrollOptions } from "@data-display/wrapper/ResponsiveScroll/logics";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Dialog } from "@styleguide";
import { type ReactNode, useLayoutEffect, useState } from "react";
import { ModalSelectCandidatesLists } from "./candidateSelection";
import { ChipsBar, type TCurrentTab } from "./candidateSelection/chipsBar";
import { FilterSection } from "./filters";
import { useAddParticipantFilters } from "./filters/logics";
import { useResponsiveScrollModalSelectCandidates } from "./responsive";

export interface IModalAddParticipantsProps {
  open: boolean;
  loading?: boolean;
  subtitle?: ReactNode;
  disabledCandidates?: { _id: ObjectId; tooltip?: string }[];
  options?: IResponsiveScrollOptions;
  onSubmit: (candidates: IPostCandidatesSearch["data"]) => void;
  onClose: () => void;
}

export const ModalSelectCandidates = ({
  open,
  loading,
  subtitle,
  disabledCandidates,
  options,
  onSubmit,
  onClose,
}: IModalAddParticipantsProps) => {
  const { filters, dispatch } = useAddParticipantFilters();
  const [selectedCandidates, setSelectedCandidates] = useState<IPostCandidatesSearch["data"]>([]);
  const [localSelectedCandidates, setLocalSelectedCandidates] = useState<IPostCandidatesSearch["data"]>([]);
  const [currentTab, setCurrentTab] = useState<TCurrentTab>("allCandidates");
  const [total, setTotal] = useState<number>(0);
  const { scrollerRef, mainContainer, setMainContainer, staticContainerRef } =
    useResponsiveScrollModalSelectCandidates();

  const handleSubmit = () => {
    onSubmit(localSelectedCandidates);
  };

  const handleClose = () => {
    onClose();
  };

  const handleChangeTabToAllCandidates = () => {
    setSelectedCandidates(localSelectedCandidates);
    setCurrentTab("allCandidates");
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only execute when open change
  useLayoutEffect(() => {
    if (open) {
      setSelectedCandidates([]);
      setLocalSelectedCandidates([]);
      setCurrentTab("allCandidates");
      setTotal(0);
      dispatch({ type: "reset" });
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: (theme) => ({ height: `calc(100% - ${theme.navbarSize})` }) }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">Ajouter des candidats</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          "&.MuiDialogContent-root": { paddingTop: "0 !important" },
        }}
      >
        <Stack
          ref={scrollerRef}
          sx={{
            overflowX: "hidden",
            overflowY: "auto",
            flex: "auto",
            ...(options?.collapseThreshold !== undefined && { overflowAnchor: "none" }),
          }}
        >
          <ResponsiveScroll
            scroller={scrollerRef.current}
            mainContainer={mainContainer}
            subContainer={null}
            staticContainer={staticContainerRef.current}
            options={options}
          >
            <Stack ref={setMainContainer} sx={(theme) => ({ gap: 2, pt: 1, bgcolor: theme.palette.background.paper })}>
              {subtitle && <Typography variant="body1">{subtitle}</Typography>}
              <FilterSection filters={filters} dispatch={dispatch} />
              <Box ref={staticContainerRef}>
                <ChipsBar
                  currentTab={currentTab}
                  total={total}
                  selectedCount={localSelectedCandidates.length}
                  onChangeToAllCandidates={handleChangeTabToAllCandidates}
                  onChangeToSelected={() => setCurrentTab("selectedCandidates")}
                  onReset={() => dispatch({ type: "reset" })}
                />
              </Box>
            </Stack>
          </ResponsiveScroll>
          <ModalSelectCandidatesLists
            filters={filters}
            currentTab={currentTab}
            disabledCandidates={disabledCandidates}
            selectedCandidates={selectedCandidates}
            localSelectedCandidates={localSelectedCandidates}
            setSelectedCandidates={setSelectedCandidates}
            setLocalSelectedCandidates={setLocalSelectedCandidates}
            setTotal={setTotal}
            scrollerRef={scrollerRef}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          Annuler
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} loading={loading}>
          Ajouter
        </Button>
      </DialogActions>
    </Dialog>
  );
};
